import type {MarketPlaceAsk} from '@/types/MarketPlace';

import {formatOptionalDate} from '../utils/date';
import {formatAmount} from '../utils/format';
import {getAskStatusDisplay} from '../utils/status';

type AskSummaryProps = {
  ask: MarketPlaceAsk;
};

const AskSummary = ({ask}: AskSummaryProps) => {
  const status = getAskStatusDisplay(ask.status, ask.expiresAt);
  const flags = ask.meta?.flags;

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 sm:px-5 py-5 sm:py-5">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <h2 className="font-semibold text-2xl mr-3">Offer #{ask.id}</h2>
        <span className={`text-sm font-medium ${status.className}`}>
          {status.label}
        </span>
        {flags?.isDeactivated && (
          <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium">
            Disabled
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <p className="text-sm text-gray-500">Created By</p>
          <p className="font-medium dark:text-gray-200">
            {ask.createdByName || '-'}
          </p>
          {ask.createdByRole && (
            <p className="text-xs text-gray-500">
              {ask.createdByRole.replace(/_/g, ' ')}
            </p>
          )}
          <p className="text-xs text-gray-500">{ask.userId}</p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Volume</p>
          <p className="font-medium dark:text-gray-200">
            {formatAmount(ask.volume)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Quote</p>
          <p className="font-medium dark:text-gray-200">
            {formatAmount(ask.quote)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Min Bid Volume</p>
          <p className="font-medium dark:text-gray-200">
            {ask.meta?.minBidVolume
              ? `${ask.meta.minBidVolume} ${ask.volume.asset}`
              : '-'}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Max Allowed Offer Price</p>
          <p className="font-medium dark:text-gray-200">
            {ask.meta?.maxAllowedOfferPrice
              ? `${ask.meta.maxAllowedOfferPrice} ${ask.quote.asset}`
              : '-'}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Created At</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDate(ask.createdAt)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Updated At</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDate(ask.updatedAt)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Resolved At</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDate(ask.resolvedAt)}
          </p>
        </div>

        <div>
          <p className="text-sm text-gray-500">Expires At</p>
          <p className="font-medium dark:text-gray-200">
            {formatOptionalDate(ask.expiresAt)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AskSummary;
